import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { Observable, of as observableOf } from 'rxjs';
import { catchError, map, mapTo, startWith, switchMap } from 'rxjs/operators';
import { AbstractServiceCatalogApiService } from 'src/app/core/services/abstract/abstract-service-catalog-api-service';
import * as actions from './actions';
import { ServiceCatalogFacade } from './facade';
import { List } from '../../../core/infrastructure/linq';
import { ServiceModel } from './model';
import { AuthService } from '../../../core/services/auth-service';
import { PermissionConstants } from '../../../core/constants/permission-constants';

@Injectable()
export class ServiceCatalogEffects {
  private service = inject(AbstractServiceCatalogApiService);
  private actions$ = inject(Actions);
  private facade = inject(ServiceCatalogFacade);
  private authService = inject(AuthService);

  getAllRequestEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType<actions.GetAllRequestAction>(actions.ActionTypes.GET_ALL_REQUEST),
      //startWith(new actions.GetAllRequestAction()),
      switchMap(action =>
        this.service.getAll().pipe(
          map(response =>
            response.isSucceeded ? new actions.GetAllSuccessAction(response.result) : new actions.FailureAction(response.errors[0])
          ),
          catchError(error => observableOf(new actions.FailureAction(error)))
        )
      )
    )
  );

  enableRequestEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType<actions.EnableRequestAction>(actions.ActionTypes.ENABLE_REQUEST),
      switchMap(action =>
        this.service.enable(action.codes).pipe(
          map(items => (items.isSucceeded ? new actions.EnableSuccessAction(items.result) : new actions.FailureAction(items.errors[0]))),
          catchError(error => observableOf(new actions.FailureAction(error)))
        )
      )
    )
  );

  disableRequestEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType<actions.DisableRequestAction>(actions.ActionTypes.DISABLE_REQUEST),
      switchMap(action =>
        this.service.disable(action.codes).pipe(
          map(items => (items.isSucceeded ? new actions.DisableSuccessAction(items.result) : new actions.FailureAction(items.errors[0]))),
          catchError(error => observableOf(new actions.FailureAction(error)))
        )
      )
    )
  );

  syncRequestEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType<actions.SyncRequestAction>(actions.ActionTypes.SYNC_REQUEST),
      switchMap(action =>
        this.service.sync().pipe(
          map(items => (items.isSucceeded ? new actions.GetAllRequestAction() : new actions.FailureAction(items.errors[0]))),
          catchError(error => observableOf(new actions.FailureAction(error)))
        )
      )
    )
  );

  filterRequestEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType<actions.FilterRequestAction>(actions.ActionTypes.FILTER_REQUEST),
      //startWith(new actions.FilterRequestAction()),
      switchMap(elon =>
        this.facade.entities$.pipe(
          map(items => {
            let result = new List<ServiceModel>(items).Where(s => s.isEnabled);

            let isShowHidden = this.authService.checkPermission(PermissionConstants.serviceCatalog_view_hidden);

            if (!isShowHidden) {
              result = result.Where(s => !s.hiddenInWebSD && !s.category.hiddenInWebSD);
            }

            return new actions.FilterSuccessAction(result.ToArray());
          }),
          catchError(error => observableOf(new actions.FailureAction(error)))
        )
      )
    )
  );

  updateServiceEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType<actions.UpdateServiceRequestAction>(actions.ActionTypes.UPDATE_SERVICE_REQUEST),
      switchMap(action =>
        this.service.updateService(action.service).pipe(
          map(response =>
            response.isSucceeded
              ? //? new actions.GetAllRequestAction()
                new actions.UpdateServiceSuccessAction(response.result)
              : new actions.FailureAction(response.errors[0])
          ),
          catchError(error => observableOf(new actions.FailureAction(error)))
        )
      )
    )
  );

  updateCategoryEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType<actions.UpdateCategoryRequestAction>(actions.ActionTypes.UPDATE_CATEGORY_REQUEST),
      switchMap(action =>
        this.service.updateCategory(action.category).pipe(
          map(items => (items.isSucceeded ? new actions.GetAllRequestAction() : new actions.FailureAction(items.errors[0]))),
          catchError(error => observableOf(new actions.FailureAction(error)))
        )
      )
    )
  );

  uploadCategoryIconEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType<actions.UploadCategoryIconRequestAction>(actions.ActionTypes.UPLOAD_CATEGORY_ICON_REQUEST),
      switchMap(action =>
        this.service.uploadCategoryIcon(action.category, action.file).pipe(
          map(items => (items.isSucceeded ? new actions.GetAllRequestAction() : new actions.FailureAction(items.errors[0]))),
          catchError(error => observableOf(new actions.FailureAction(error)))
        )
      )
    )
  );

  deleteCategoryIconEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType<actions.DeleteCategoryIconRequestAction>(actions.ActionTypes.DELETE_CATEGORY_ICON_REQUEST),
      switchMap(action =>
        this.service.deleteCategoryIcon(action.category).pipe(
          map(items => (items.isSucceeded ? new actions.GetAllRequestAction() : new actions.FailureAction(items.errors[0]))),
          catchError(error => observableOf(new actions.FailureAction(error)))
        )
      )
    )
  );

  uploadMasterCategoryIconEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType<actions.UploadMasterCategoryIconRequestAction>(actions.ActionTypes.UPLOAD_MASTER_CATEGORY_ICON_REQUEST),
      switchMap(action =>
        this.service.uploadMasterCategoryIcon(action.masterCategory, action.file).pipe(
          map(items => (items.isSucceeded ? new actions.GetAllRequestAction() : new actions.FailureAction(items.errors[0]))),
          catchError(error => observableOf(new actions.FailureAction(error)))
        )
      )
    )
  );

  deleteMasterCategoryIconEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType<actions.DeleteMasterCategoryIconRequestAction>(actions.ActionTypes.DELETE_MASTER_CATEGORY_ICON_REQUEST),
      switchMap(action =>
        this.service.deleteMasterCategoryIcon(action.masterCategory).pipe(
          map(items => (items.isSucceeded ? new actions.GetAllRequestAction() : new actions.FailureAction(items.errors[0]))),
          catchError(error => observableOf(new actions.FailureAction(error)))
        )
      )
    )
  );

  upPriorityCategoryEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType<actions.UpPriorityCategoryRequestAction>(actions.ActionTypes.UP_PRIORITY_CATEGORY_REQUEST),
      switchMap(action =>
        this.service.upPriorityCategory(action.category).pipe(
          map(items => (items.isSucceeded ? new actions.GetAllRequestAction() : new actions.FailureAction(items.errors[0]))),
          catchError(error => observableOf(new actions.FailureAction(error)))
        )
      )
    )
  );

  downPriorityCategoryEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType<actions.DownPriorityCategoryRequestAction>(actions.ActionTypes.DOWN_PRIORITY_CATEGORY_REQUEST),
      switchMap(action =>
        this.service.downPriorityCategory(action.category).pipe(
          map(items => (items.isSucceeded ? new actions.GetAllRequestAction() : new actions.FailureAction(items.errors[0]))),
          catchError(error => observableOf(new actions.FailureAction(error)))
        )
      )
    )
  );

  upPriorityMasterCategoryEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType<actions.UpPriorityMasterCategoryRequestAction>(actions.ActionTypes.UP_PRIORITY_MASTER_CATEGORY_REQUEST),
      switchMap(action =>
        this.service.upPriorityMasterCategory(action.masterCategory).pipe(
          map(items => (items.isSucceeded ? new actions.GetAllRequestAction() : new actions.FailureAction(items.errors[0]))),
          catchError(error => observableOf(new actions.FailureAction(error)))
        )
      )
    )
  );

  downPriorityMasterCategoryEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType<actions.DownPriorityMasterCategoryRequestAction>(actions.ActionTypes.DOWN_PRIORITY_MASTER_CATEGORY_REQUEST),
      switchMap(action =>
        this.service.downPriorityMasterCategory(action.masterCategory).pipe(
          map(items => (items.isSucceeded ? new actions.GetAllRequestAction() : new actions.FailureAction(items.errors[0]))),
          catchError(error => observableOf(new actions.FailureAction(error)))
        )
      )
    )
  );
}
